import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import Styles from "./index.module.scss";
import NFTModal from "@/components/NFTModal";
import Service from "../service";
import {hideLoading, showLoading} from "@/components/loading";
import {Toast} from "antd-mobile";

/**
 * 积分活动 — 选择回收藏品（设计稿：请选择藏品）
 * @route /culturalExchange_integral/recycleSelect
 */
const RecycleSelect = () => {
  const navigate = useNavigate();
  const [collectionList, setCollectionList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [sheetNum, setSheetNum] = useState(0); // 弹窗中用户可调整的回收数量（提交用）
  const [sheetMaxNum, setSheetMaxNum] = useState(0); // 弹窗中数量上限（来自接口 count）

  const previewItems = useMemo(() => selectedItems, [selectedItems]);
  const recoveryPointPer = useMemo(() => {
    const v = selectedItems?.[0]?.recoveryPoint ?? selectedItems?.[0]?.recoverypoint;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }, [selectedItems]);

  const totalRecoveryPoints = useMemo(() => {
    const num = Number(sheetNum) || 0;
    return Math.max(0, num * recoveryPointPer);
  }, [sheetNum, recoveryPointPer]);

  useEffect(() => {
    getData();
  }, []);

  const getData = async (withLoading = true) => {
    try {
      if (withLoading) showLoading();
      const resdata = await Service.getRecoveryCollCount();
      if (resdata.code !== "000000") throw resdata.msg;
      // 接口返回：data: [{count,nftimage,collectionId,collectionName}]
      const list = Array.isArray(resdata.data)
        ? resdata.data
        : (resdata.data?.list || resdata.data?.data?.list || []);
      // 统一字段名，保证后续渲染/选择逻辑可用
      setCollectionList(
        (list || []).map((it) => ({
          ...it,
          nftImage: it.nftImage || it.nftimage || it.nftImg || it.image,
        })),
      );
    } catch (e) {
      Toast.show(e || "加载失败");
      setCollectionList([]);
    } finally {
      if (withLoading) hideLoading();
    }
  };

  const handleBack = () => {
    if (window.JsBridge.hasWebViewBridge()) {
        window.JsBridge.back();
      } else {
        navigate(-1);
      }
  };

  const toRecycleRecord = () => {
    navigate("/culturalExchange_integral/recycleRecord");
  };

  const handleSelectItem = async (item) => {
    const selectedCollectionId = selectedItems[0]?.collectionId;
    const isSelected = selectedCollectionId != null && selectedCollectionId === item.collectionId;
    if (isSelected) {
      const list = [];
      setSelectedItems(list);
    } else {
      const list = [item];
      setSelectedItems(list);
    }
  };

  const handleRecycle = () => {
    if (selectedItems.length === 0) {
      Toast.show("请至少选择一个藏品");
      return;
    }
    const maxNum = Number(selectedItems[0]?.count ?? 0) || 0;
    setSheetMaxNum(maxNum);
    setSheetNum(maxNum); // 默认就是接口返回的总数量
    setSheetVisible(true);
  };

  const closeRecycleSheet = () => {
    setSheetVisible(false);
    setSheetNum(0);
    setSheetMaxNum(0);
  };

  const handleSheetConfirmRecycle = async () => {
    const selected = selectedItems[0];
    const num = Number(sheetNum) || 0;
    if (!num) {
      Toast.show("请先选择要回收的藏品");
      return;
    }
    try {
      showLoading();
      const collectionId = selected?.collectionId;
      const res = await Service.recoveryCtrlPrize({
        collectionId,
        num
      });
      if (res.code !== "000000") {
        Toast.show(res.msg);
        return;
      }
      await getData(false);
      setSheetVisible(false);
      setSelectedItems([]);
      Toast.show("回收成功");
    } catch (error) {
      Toast.show(error?.msg || "回收失败, 请稍后重试");
    } finally {
      hideLoading();
    }
  };

  return (
    <div className={Styles.recycleSelect}>
      {/* 与 exchangeRecord（领取记录）一致：状态栏占位 + 导航栏 */}
      <div className={Styles.statusBar} />
      <div className={Styles.navBar}>
        <div
          className={Styles.backButton}
          onClick={handleBack}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleBack()}
          aria-label="返回"
        />
        <div className={Styles.navBarMid} />
        <span
          className={Styles.navRight}
          onClick={toRecycleRecord}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && toRecycleRecord()}
        >
          回收记录{">"}
        </span>
      </div>

      <div className={Styles.content}>
        <main className={Styles.main}>
          <h1 className={Styles.pageTitle}>请选择藏品</h1>

          {collectionList.length > 0 && (
            <div className={Styles.grid}>
              {collectionList.map((item) => {
                const isSelected = selectedItems.some((s) => s.collectionId === item.collectionId);
                const pts = item.count;
                return (
                  <div
                    key={item.collectionId || item.id || item.nftImage}
                    className={Styles.card}
                    onClick={() => handleSelectItem(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleSelectItem(item)}
                  >
                    <div className={`${Styles.checkBox} ${isSelected ? Styles.checkBoxOn : ""}`}>
                      {isSelected && (
                        <img src={require("@/assets/img/culturalExchange_maotaisecond/selected.png")} alt="" />
                      )}
                    </div>
                    <div className={Styles.cardMedia}>
                      {pts != null && !Number.isNaN(pts) && (
                        <div className={Styles.pointsTag}>{pts}</div>
                      )}
                      <div className={Styles.cardImage}>
                        <img src={item.nftImage} alt="" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {collectionList.length === 0 && (
            <div className={Styles.empty}>
              <p>暂无可回收藏品</p>
            </div>
          )}


          </main>
          <div className={Styles.recycleBtnBox}>
              <button type="button" className={Styles.recycleBtn} onClick={handleRecycle}>
                <span className={Styles.recycleBtnText}>回收藏品</span>
              </button>
            </div>
        </div>

      <NFTModal
        visible={sheetVisible}
        closeOnMaskClick
        onClose={closeRecycleSheet}
        content={
          <div className={Styles.recycleSheet}>
            <button
              type="button"
              className={Styles.recycleSheetClose}
              onClick={closeRecycleSheet}
              aria-label="关闭"
            >
              ×
            </button>
            <div className={Styles.recycleSheetBody}>
              <p className={Styles.recycleSheetLine}>回收后藏品将被销毁</p>
              <p className={Styles.recycleSheetLine}>
                获得
                <span className={Styles.recycleSheetPoints}>{totalRecoveryPoints}</span>
                积分
              </p>
              <div className={Styles.recycleSheetStepper}>
                <button
                  type="button"
                  className={`${Styles.stepperBtn} ${Styles.stepperBtnMinus}`}
                  disabled={sheetNum <= 1}
                  onClick={() => setSheetNum((n) => Math.max(1, (Number(n) || 0) - 1))}
                  aria-label="减少数量"
                >
                  -
                </button>
                <div className={Styles.stepperValue}>{sheetNum}</div>
                <button
                  type="button"
                  className={`${Styles.stepperBtn} ${Styles.stepperBtnPlus}`}
                  disabled={sheetNum >= sheetMaxNum}
                  onClick={() => setSheetNum((n) => Math.min(sheetMaxNum, (Number(n) || 0) + 1))}
                  aria-label="增加数量"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className={Styles.recycleSheetConfirm}
                onClick={handleSheetConfirmRecycle}
              >
                确认回收
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default RecycleSelect;
